import React, { useCallback, useEffect, useState } from 'react';
import adminApi from '../api/client';

type UserSummary = { id: string | null; name: string | null };
type PostContext = {
  id: string;
  content: string;
  category: string | null;
  author: UserSummary | null;
  is_flagged: boolean;
  is_removed: boolean;
};
type CommentContext = {
  id: string;
  content: string;
  author: UserSummary | null;
  is_deleted: boolean;
  parent_id: string | null;
};
type Report = {
  id: string;
  content_type: 'post' | 'wisdom_comment';
  content_id: string;
  reason: string;
  description: string | null;
  status: string;
  created_at: string | null;
  reporter: UserSummary | null;
  target: {
    missing: boolean;
    post: PostContext | null;
    comment: CommentContext | null;
    parent_comment: CommentContext | null;
  };
  resolution_action: string | null;
  resolution_note: string | null;
  resolved_by: UserSummary | null;
  resolved_at: string | null;
};
type StatusFilter = 'pending' | 'resolved' | 'all';

export default function WisdomReports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Report | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.get('/admin/wisdom/reports', {
        params: { status, page, limit: 20 },
      });
      const nextReports: Report[] = response.data.reports ?? [];
      setReports(nextReports);
      setTotal(response.data.total ?? 0);
      setSelectedId((current) => (
        current && nextReports.some((report) => report.id === current)
          ? current
          : nextReports[0]?.id ?? null
      ));
    } catch (error: any) {
      setMessage({ text: error.response?.data?.detail || 'Could not load Wisdom reports.', error: true });
    } finally {
      setLoading(false);
    }
  }, [page, status]);

  useEffect(() => { void fetchQueue(); }, [fetchQueue]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setResolutionNote('');
      return;
    }
    let active = true;
    adminApi.get(`/admin/wisdom/reports/${selectedId}`)
      .then((response) => {
        if (active) {
          setDetail(response.data.report);
          setResolutionNote(response.data.report.resolution_note ?? '');
        }
      })
      .catch((error: any) => {
        if (active) setMessage({ text: error.response?.data?.detail || 'Could not load this report.', error: true });
      });
    return () => { active = false; };
  }, [selectedId]);

  const resolve = async (action: 'dismiss_report' | 'remove_content') => {
    if (!detail || !resolutionNote.trim()) {
      setMessage({ text: 'Add a resolution note before taking action.', error: true });
      return;
    }
    if (action === 'remove_content' && !window.confirm('Soft-remove this Wisdom content?')) return;
    setResolving(true);
    try {
      const response = await adminApi.post(`/admin/wisdom/reports/${detail.id}/review`, {
        action,
        resolution_note: resolutionNote.trim(),
      });
      setDetail((current) => current ? { ...current, ...response.data.report } : current);
      setMessage({
        text: action === 'dismiss_report' ? 'Report dismissed.' : 'Content removed and related reports resolved.',
        error: false,
      });
      await fetchQueue();
    } catch (error: any) {
      setMessage({ text: error.response?.data?.detail || 'Could not resolve this report.', error: true });
    } finally {
      setResolving(false);
    }
  };

  const isCommentReport = (report: Report) => report.content_type === 'wisdom_comment';
  const targetText = (report: Report) => isCommentReport(report)
    ? report.target.comment?.content
    : report.target.post?.content;
  const targetLabel = (report: Report) => isCommentReport(report)
    ? report.target.comment?.parent_id ? 'Reply' : 'Comment'
    : 'Post';
  const targetUnavailableForRemoval = (report: Report) => {
    if (report.target.missing) return true;
    const post = report.target.post;
    if (!post || post.is_flagged || post.is_removed) return true;
    if (!isCommentReport(report)) return false;
    return !report.target.comment || report.target.comment.is_deleted;
  };

  return (
    <div className="min-h-screen bg-[#F1EFE8] p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#085041]">Wisdom Reports</h1>
            <p className="mt-1 text-sm text-gray-600">Review reports for Wisdom posts, comments, and replies.</p>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            Status
            <select
              className="rounded-lg border border-gray-300 bg-white px-3 py-2"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as StatusFilter);
                setPage(1);
                setSelectedId(null);
              }}
            >
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="all">All</option>
            </select>
          </label>
        </div>

        {message && (
          <button
            className={`mb-4 w-full rounded-lg border px-4 py-3 text-left ${message.error ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800'}`}
            onClick={() => setMessage(null)}
          >
            {message.text} <span className="float-right">×</span>
          </button>
        )}

        <div className="grid gap-6 xl:grid-cols-[minmax(320px,0.85fr)_minmax(500px,1.5fr)]">
          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <h2 className="font-semibold text-gray-800">{total} report{total === 1 ? '' : 's'}</h2>
              {loading && <span className="text-xs text-gray-500">Loading…</span>}
            </div>
            {!reports.length && !loading ? (
              <p className="p-8 text-center text-sm text-gray-500">No Wisdom reports in this view.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {reports.map((report) => (
                  <button
                    key={report.id}
                    className={`block w-full px-5 py-4 text-left hover:bg-emerald-50 ${selectedId === report.id ? 'bg-emerald-50 ring-1 ring-inset ring-emerald-200' : ''}`}
                    onClick={() => setSelectedId(report.id)}
                  >
                    <div className="flex justify-between gap-3">
                      <span className="font-semibold text-[#085041]">{targetLabel(report)} · {report.reason}</span>
                      <span className="shrink-0 text-xs uppercase text-gray-500">{report.status}</span>
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm text-gray-700">{targetText(report) || 'Reported content is unavailable.'}</p>
                    <p className="mt-2 text-xs text-gray-500">By {report.reporter?.name || report.reporter?.id || 'Unknown'} · {report.created_at ? new Date(report.created_at).toLocaleString() : 'Date unavailable'}</p>
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3 text-sm text-gray-600">
              <span>Page {page}</span>
              <div className="flex gap-2">
                <button className="rounded border px-3 py-1 disabled:opacity-40" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
                <button className="rounded border px-3 py-1 disabled:opacity-40" disabled={page * 20 >= total} onClick={() => setPage((value) => value + 1)}>Next</button>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            {!detail ? (
              <p className="py-16 text-center text-gray-500">Select a report to review.</p>
            ) : (
              <>
                <div className="mb-5 flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 pb-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">{targetLabel(detail)} report · {detail.status}</p>
                    <h2 className="mt-1 text-xl font-bold text-gray-900">{detail.reason}</h2>
                  </div>
                  <p className="text-right text-xs text-gray-500">{detail.created_at ? new Date(detail.created_at).toLocaleString() : ''}<br />Report ID: {detail.id}</p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-lg bg-gray-50 p-4">
                    <h3 className="text-xs font-semibold uppercase text-gray-500">Reporter</h3>
                    <p className="mt-1 font-medium text-gray-900">{detail.reporter?.name || 'Unknown user'}</p>
                    <p className="text-sm text-gray-600">{detail.reporter?.id || 'No reporter ID'}</p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4">
                    <h3 className="text-xs font-semibold uppercase text-gray-500">Reported author</h3>
                    <p className="mt-1 font-medium text-gray-900">
                      {(isCommentReport(detail) ? detail.target.comment?.author : detail.target.post?.author)?.name || 'Unknown user'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {(isCommentReport(detail) ? detail.target.comment?.author : detail.target.post?.author)?.id || ''}
                    </p>
                  </div>
                </div>

                <div className="mt-5 space-y-4">
                  {isCommentReport(detail) && detail.target.parent_comment && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <h3 className="text-xs font-semibold uppercase text-amber-800">Parent comment context</h3>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{detail.target.parent_comment.content}</p>
                      <p className="mt-2 text-xs text-gray-500">{detail.target.parent_comment.author?.name || 'Unknown user'}</p>
                    </div>
                  )}
                  <div className="rounded-lg border border-gray-200 p-4">
                    <h3 className="text-xs font-semibold uppercase text-gray-500">Reported {targetLabel(detail).toLowerCase()}</h3>
                    <p className="mt-2 whitespace-pre-wrap text-gray-900">{targetText(detail) || (detail.target.missing ? 'Reported content is unavailable.' : 'No content')}</p>
                  </div>
                  {isCommentReport(detail) && detail.target.post && (
                    <div className="rounded-lg bg-gray-50 p-4">
                      <h3 className="text-xs font-semibold uppercase text-gray-500">Parent Wisdom post context</h3>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{detail.target.post.content}</p>
                      <p className="mt-2 text-xs text-gray-500">{detail.target.post.author?.name || 'Unknown user'} · {detail.target.post.category || 'Wisdom'}</p>
                    </div>
                  )}
                  {detail.description && (
                    <div className="rounded-lg bg-gray-50 p-4">
                      <h3 className="text-xs font-semibold uppercase text-gray-500">Reporter note</h3>
                      <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">{detail.description}</p>
                    </div>
                  )}
                </div>

                {detail.status === 'pending' ? (
                  <div className="mt-6 border-t border-gray-100 pt-5">
                    <label className="block text-sm font-semibold text-gray-800" htmlFor="wisdom-resolution-note">Resolution note (required)</label>
                    <textarea
                      id="wisdom-resolution-note"
                      className="mt-2 min-h-24 w-full rounded-lg border border-gray-300 p-3 text-sm outline-none focus:border-emerald-600"
                      maxLength={2000}
                      value={resolutionNote}
                      onChange={(event) => setResolutionNote(event.target.value)}
                      placeholder="Record why this report is dismissed or why the content is being removed."
                    />
                    <div className="mt-3 flex flex-wrap justify-end gap-3">
                      <button className="rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 disabled:opacity-50" disabled={resolving || !resolutionNote.trim()} onClick={() => void resolve('dismiss_report')}>Dismiss report</button>
                      <button className="rounded-lg bg-red-700 px-4 py-2 font-medium text-white disabled:opacity-50" disabled={resolving || !resolutionNote.trim() || targetUnavailableForRemoval(detail)} onClick={() => void resolve('remove_content')}>{resolving ? 'Saving…' : 'Remove content'}</button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm">
                    <p className="font-semibold text-emerald-900">Resolved: {detail.resolution_action || 'reviewed'}</p>
                    <p className="mt-1 text-emerald-800">{detail.resolution_note || 'No note recorded'}</p>
                    <p className="mt-2 text-xs text-emerald-800">{detail.resolved_by?.name || detail.resolved_by?.id || 'Admin'} · {detail.resolved_at ? new Date(detail.resolved_at).toLocaleString() : 'Time unavailable'}</p>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
